import { ref } from 'vue';
import axios from 'axios';
import { useCurrencyFormatter } from '@/utils/currencyFormatter.js';

// Shared state
const cartCount = ref(0);
const cartItems = ref([]);
const isLoadingCart = ref(false);

export function useCart() {
    const loading = ref(false);

    // Load cart count
    const loadCartCount = async () => {
        try {
            const response = await axios.get(route('client.carts.count'));
            cartCount.value = response.data.count;
            return cartCount.value;
        } catch (error) {
            console.error('Error loading cart count:', error);
            return 0;
        }
    };

    // Load full cart
    const loadCart = async () => {
        // Prevent multiple simultaneous requests (but allow sequential ones)
        if (isLoadingCart.value) {
            // Wait for the current request to finish instead of making a new one
            return new Promise((resolve) => {
                const checkInterval = setInterval(() => {
                    if (!isLoadingCart.value) {
                        clearInterval(checkInterval);
                        resolve({ items: cartItems.value, total_links: cartCount.value });
                    }
                }, 50);
            });
        }

        isLoadingCart.value = true;
        loading.value = true;
        try {
            const response = await axios.get(route('client.carts.index'));
            cartItems.value = response.data.items;
            cartCount.value = response.data.total_links;
            
            if (response.data.currencies && response.data.exchangeValues) {
                const { updateExchangeRates } = useCurrencyFormatter();
                updateExchangeRates(response.data.currencies, response.data.exchangeValues);
            }
            
            return response.data;
        } catch (error) {
            console.error('Error loading cart:', error);
            throw error;
        } finally {
            loading.value = false;
            isLoadingCart.value = false;
        }
    };

    // Add item to cart
    const addToCart = async (siteId, quantity = 1) => {
        loading.value = true;
        try {
            const response = await axios.post(route('client.carts.store'), {
                site_id: siteId,
                quantity: quantity
            });

            cartItems.value = response.data.items;
            cartCount.value = response.data.total_links;

            if (response.data.currencies && response.data.exchangeValues) {
                const { updateExchangeRates } = useCurrencyFormatter();
                updateExchangeRates(response.data.currencies, response.data.exchangeValues);
            }

            // Dispatch event for other components
            window.dispatchEvent(new CustomEvent('cart-item-added', {
                detail: { count: cartCount.value }
            }));

            return response.data;
        } catch (error) {
            console.error('Error adding to cart:', error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    // Update item quantity
    const updateQuantity = async (siteId, quantity) => {
        loading.value = true;
        try {
            const response = await axios.put(route('client.carts.update', siteId), {
                quantity: quantity
            });

            cartItems.value = response.data.items;
            cartCount.value = response.data.total_links;

            // Dispatch event for other components
            window.dispatchEvent(new CustomEvent('cart-updated', {
                detail: { count: cartCount.value }
            }));

            return response.data;
        } catch (error) {
            console.error('Error updating quantity:', error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    // Remove item from cart
    const removeFromCart = async (siteId) => {
        loading.value = true;
        try {
            const response = await axios.delete(route('client.carts.destroy', siteId));

            cartItems.value = response.data.items;
            cartCount.value = response.data.total_links;

            // Dispatch event for other components
            window.dispatchEvent(new CustomEvent('cart-updated', {
                detail: { count: cartCount.value }
            }));

            return response.data;
        } catch (error) {
            console.error('Error removing from cart:', error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    const clearCart = async () => {
        loading.value = true;
        try {
            const response = await axios.delete(route('client.carts.clear'));

            cartItems.value = [];
            cartCount.value = 0;

            window.dispatchEvent(new CustomEvent('cart-updated', {
                detail: { count: 0 }
            }));

            return response.data;
        } catch (error) {
            console.error('Error clearing cart:', error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    // Get quantity for a specific site
    const getItemQuantity = (siteId) => {
        const item = cartItems.value.find(item => item.id === siteId);
        return item ? item.quantity : 0;
    };

    // Check if item is in cart
    const isInCart = (siteId) => {
        return cartItems.value.some(item => item.id === siteId);
    };

    return {
        cartCount,
        cartItems,
        loading,
        loadCartCount,
        loadCart,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        getItemQuantity,
        isInCart
    };
}
